/**
 * Admin V5 — One-click Digital SAT content restore.
 * Bundle already ships with the app: /app/backend/data/dsat_content_bundle.json
 * Endpoints (admin-only):
 *   GET  /api/exam/v2/content-bundle/dsat/manifest
 *   POST /api/exam/v2/content-bundle/dsat/import?dry_run=true|false
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Database, ShieldCheck, PlayCircle, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

export default function DSATContentRestoreCard() {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dryReport, setDryReport] = useState(null);
  const [liveReport, setLiveReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadManifest = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get('/api/exam/v2/content-bundle/dsat/manifest');
      setManifest(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Manifest load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadManifest(); }, [loadManifest]);

  const runDry = async () => {
    setBusy(true); setDryReport(null); setLiveReport(null);
    try {
      const r = await ax.post('/api/exam/v2/content-bundle/dsat/import?dry_run=true');
      setDryReport(r.data.report);
      const rep = r.data.report;
      if (rep.questions.malformed > 0 || rep.tests.orphaned > 0
          || rep.questions.errors.length > 0 || rep.tests.errors.length > 0) {
        toast.warning('Dry-run found issues — live import is blocked.');
      } else {
        toast.success('Dry-run OK. Review counts, then confirm live import.');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Dry-run failed'); }
    finally { setBusy(false); }
  };

  const runLive = async () => {
    if (!dryReport) return;
    if (!window.confirm(
      `LIVE IMPORT — confirm?\n\nThis will UPSERT into production:\n` +
      `• ${dryReport.questions.inserted + dryReport.questions.updated} questions\n` +
      `• ${dryReport.tests.inserted + dryReport.tests.updated} tests\n` +
      `• exam config + challenge config\n\n` +
      `It will NOT touch attempts, assignments, users, or emails.\n` +
      `Click OK to proceed.`)) return;
    setBusy(true); setLiveReport(null);
    try {
      const r = await ax.post('/api/exam/v2/content-bundle/dsat/import?dry_run=false');
      setLiveReport(r.data.report);
      toast.success('Digital SAT content imported to production.');
      loadManifest();
    } catch (e) { toast.error(e.response?.data?.detail || 'Live import failed'); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <Card className="p-4 bg-gray-50 border-dashed"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading manifest…</Card>;
  }

  const dbHasContent = (manifest?.current_db?.questions || 0) > 0;
  const canLive = dryReport
    && dryReport.questions.malformed === 0
    && dryReport.tests.orphaned === 0
    && (dryReport.questions.errors || []).length === 0
    && (dryReport.tests.errors || []).length === 0;

  return (
    <Card className="p-4 border-2 border-red-200 bg-gradient-to-br from-red-50 to-white" data-testid="dsat-restore-card">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-red-700 p-2">
          <Database className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-base font-bold text-gray-900">Digital SAT content restore</div>
            {dbHasContent
              ? <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Content present in DB</Badge>
              : <Badge className="bg-amber-100 text-amber-900"><AlertTriangle className="h-3 w-3 mr-1" /> Live DB is empty</Badge>}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            One-click import of the approved Digital SAT bundle shipped with this build. Safe to re-run.
            Never touches attempts, assignments, users or emails.
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
            <Stat label="Bundle questions" value={manifest.bundle.questions} accent />
            <Stat label="Bundle tests" value={manifest.bundle.tests} accent />
            <Stat label="DB questions now" value={manifest.current_db.questions} />
            <Stat label="DB tests now" value={manifest.current_db.tests} />
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Button size="sm" onClick={runDry} disabled={busy}
              className="bg-gray-900 hover:bg-black text-white"
              data-testid="dsat-restore-dry">
              {busy && !liveReport ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
              1 · Dry-run
            </Button>
            <Button size="sm" onClick={runLive} disabled={!canLive || busy}
              className="bg-red-700 hover:bg-red-800 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="dsat-restore-live">
              {busy && dryReport ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <PlayCircle className="h-3.5 w-3.5 mr-1" />}
              2 · Import to live
            </Button>
            {dryReport && !canLive && (
              <span className="text-[11px] text-rose-700 font-bold">
                Blocked — malformed or orphaned records. See report below.
              </span>
            )}
            {liveReport && <Badge className="bg-emerald-100 text-emerald-800">Live import complete</Badge>}
          </div>

          {(dryReport || liveReport) && (
            <div className="mt-3 border rounded p-3 bg-white text-[12px]" data-testid="dsat-restore-report">
              <div className="text-xs font-bold text-gray-800 mb-1">
                {liveReport ? 'Live import report' : 'Dry-run report'}
              </div>
              <ReportBlock report={liveReport || dryReport} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-2 rounded border ${accent ? 'bg-gray-900 text-white border-gray-900' : 'bg-white'}`}>
      <div className={`text-[9px] uppercase tracking-wider font-bold ${accent ? 'text-white/60' : 'text-gray-500'}`}>{label}</div>
      <div className={`text-lg font-bold ${accent ? 'text-white' : 'text-gray-900'}`}>{value ?? 0}</div>
    </div>
  );
}

function ReportBlock({ report }) {
  if (!report) return null;
  const q = report.questions || {}; const t = report.tests || {};
  return (
    <div className="space-y-1">
      <div>Questions — inserted <b>{q.inserted}</b> · updated <b>{q.updated}</b> · skipped {q.skipped} · malformed <b className={q.malformed ? 'text-rose-700' : ''}>{q.malformed}</b></div>
      <div>Tests — inserted <b>{t.inserted}</b> · updated <b>{t.updated}</b> · skipped {t.skipped} · orphaned <b className={t.orphaned ? 'text-rose-700' : ''}>{t.orphaned}</b></div>
      <div>Exam config: <b>{report.exam_config?.status || '—'}</b> · Challenge config: <b>{report.challenge_config?.status || '—'}</b></div>
      {(q.errors?.length > 0 || t.errors?.length > 0) && (
        <details className="mt-1">
          <summary className="text-rose-700 cursor-pointer">Errors ({(q.errors?.length || 0) + (t.errors?.length || 0)})</summary>
          <pre className="text-[10px] bg-rose-50 border border-rose-200 p-2 rounded mt-1 max-h-40 overflow-auto">
            {JSON.stringify({ questions: q.errors, tests: t.errors }, null, 2)}
          </pre>
        </details>
      )}
      {report.warning && <div className="text-rose-700 mt-1"><AlertTriangle className="h-3 w-3 inline mr-1" /> {report.warning}</div>}
    </div>
  );
}
